import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { formatReportType } from '../utils/safetyReportLabels';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function DeletedAccounts() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('archives');
  const [accounts, setAccounts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [graceFilter, setGraceFilter] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [purgeNotes, setPurgeNotes] = useState('');
  const [purging, setPurging] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPurging, setBulkPurging] = useState(false);

  useEffect(() => {
    const q = searchParams.get('search');
    if (q && q !== search) setSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'archives') {
      fetchAccounts();
    } else {
      fetchAuditLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, dateFrom, dateTo, graceFilter, activeTab]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (graceFilter === 'purge') {
        params.purgeEligible = 'true';
      } else if (graceFilter) {
        params.inGracePeriod = graceFilter;
      }

      const response = await adminAPI.getDeletedAccounts(params);
      setAccounts(response.data.accounts || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load deleted accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const response = await adminAPI.getDeletedAccountAuditLogs(params);
      setAuditLogs(response.data.auditLogs || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await adminAPI.getDeletedAccountById(id);
      setSelectedAccount(response.data.account);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load account details');
    } finally {
      setDetailLoading(false);
    }
  };

  const openAuditLogDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await adminAPI.getDeletedAccountAuditLogById(id);
      setSelectedAuditLog(response.data.auditLog);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load audit log');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedAccount(null);
    setSelectedAuditLog(null);
    setPurgeNotes('');
  };

  const handlePurge = async (archiveId) => {
    if (
      !window.confirm(
        'Permanently purge this archive? Messages, posts, photos, and verification media will be deleted. A minimal legal audit trace will be kept.'
      )
    ) {
      return;
    }

    try {
      setPurging(true);
      const response = await adminAPI.purgeDeletedAccount(archiveId, {
        notes: purgeNotes.trim() || undefined,
      });
      alert(response.data?.message || 'Archive purged.');
      closeDetail();
      fetchAccounts();
    } catch (err) {
      if (err.response?.data?.code === 'GRACE_PERIOD_ACTIVE') {
        const override = window.confirm(
          `${err.response.data.error}\n\nForce purge anyway? (Use only for compliance/legal reasons.)`
        );
        if (override) {
          try {
            await adminAPI.purgeDeletedAccount(archiveId, {
              notes: purgeNotes.trim() || 'Admin override — grace period not ended',
              adminOverrideGrace: true,
            });
            alert('Archive purged with grace override.');
            closeDetail();
            fetchAccounts();
          } catch (innerErr) {
            alert(innerErr.response?.data?.error || 'Failed to purge archive');
          }
        }
      } else {
        alert(err.response?.data?.error || 'Failed to purge archive');
      }
    } finally {
      setPurging(false);
    }
  };

  const toggleSelectId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const eligibleIds = accounts.filter((a) => a.gracePeriodExpired).map((a) => a._id);
    if (eligibleIds.length === 0) return;
    const allSelected = eligibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !eligibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...eligibleIds])]);
    }
  };

  const handleBulkPurge = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Permanently purge ${selectedIds.length} archive(s)? Messages, photos, and media will be deleted. Legal audit traces will be kept.`
      )
    ) {
      return;
    }

    try {
      setBulkPurging(true);
      const response = await adminAPI.bulkPurgeDeletedAccounts({
        ids: selectedIds,
        notes: 'Bulk purge from admin panel',
      });
      alert(response.data?.message || 'Bulk purge complete');
      setSelectedIds([]);
      fetchAccounts();
    } catch (err) {
      alert(err.response?.data?.error || 'Bulk purge failed');
    } finally {
      setBulkPurging(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setGraceFilter('');
    setPage(1);
  };

  return (
    <div>
      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-sm">
        <strong>Self-service deletions.</strong> These archives are created when a user deletes
        their account in the app. This is separate from <strong>Users → Soft-deleted</strong>,
        which only lists accounts marked deleted by an admin.
      </div>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => {
            setActiveTab('archives');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl font-semibold ${
            activeTab === 'archives'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Archives (grace period)
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab('audit-logs');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl font-semibold ${
            activeTab === 'audit-logs'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Purged — legal audit logs
        </button>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <h1 className="text-4xl font-bold text-gray-900">
          {activeTab === 'archives' ? 'Deleted Accounts' : 'Audit Logs'}
        </h1>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search email or name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none"
          />
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {activeTab === 'archives' && (
      <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Deleted from</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Deleted to</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Grace period</label>
          <select
            value={graceFilter}
            onChange={(e) => {
              setGraceFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="">All</option>
            <option value="true">In grace period</option>
            <option value="false">Grace expired</option>
            <option value="purge">Ready to purge</option>
          </select>
        </div>
      </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          {activeTab === 'archives' ? 'Loading deleted accounts...' : 'Loading audit logs...'}
        </div>
      ) : activeTab === 'audit-logs' ? (
        <>
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800">
            Legal audit traces kept after purge. No messages, photos, or verification media —
            only identity metadata, IP/device, counts, and report summaries.
          </div>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Profile</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Purged</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Platform</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">City</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">IP</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Messages</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-500">
                      No audit logs yet. Purge an expired archive to create one.
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((row) => (
                    <tr key={row._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{row.profileName || '—'}</td>
                      <td className="px-4 py-3 text-sm">{row.email}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(row.permanentDeletionDate)}</td>
                      <td className="px-4 py-3 text-sm uppercase">{row.platform || '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        {row.city ? `${row.city}${row.state ? `, ${row.state}` : ''}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono">{row.registrationIp || '—'}</td>
                      <td className="px-4 py-3">{row.contentCounts?.messages ?? 0}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openAuditLogDetail(row._id)}
                          className="text-pink-600 font-semibold hover:underline"
                        >
                          View trace
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 rounded-lg bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 rounded-lg bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Bulk action bar */}
          {selectedIds.length > 0 && (
            <div className="mb-4 flex items-center gap-4 bg-pink-50 border border-pink-200 rounded-xl px-4 py-3">
              <span className="text-sm font-semibold text-pink-700">
                {selectedIds.length} selected
              </span>
              <button
                type="button"
                onClick={handleBulkPurge}
                disabled={bulkPurging}
                className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {bulkPurging ? 'Purging...' : `Purge ${selectedIds.length} Archive(s)`}
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear selection
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={
                        accounts.filter((a) => a.gracePeriodExpired).length > 0 &&
                        accounts
                          .filter((a) => a.gracePeriodExpired)
                          .every((a) => selectedIds.includes(a._id))
                      }
                      className="rounded"
                      title="Select all purge-eligible"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Profile</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Platform</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">City</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Deleted</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Grace ends</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Messages</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-gray-500">
                      No deleted account archives found
                    </td>
                  </tr>
                ) : (
                  accounts.map((row) => (
                    <tr key={row._id} className={`border-b hover:bg-gray-50 ${selectedIds.includes(row._id) ? 'bg-pink-50/50' : ''}`}>
                      <td className="px-3 py-3">
                        {row.gracePeriodExpired ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row._id)}
                            onChange={() => toggleSelectId(row._id)}
                            className="rounded"
                          />
                        ) : (
                          <input type="checkbox" disabled className="rounded opacity-30" title="Grace period not expired" />
                        )}
                      </td>
                      <td className="px-4 py-3">{row.profileName || '—'}</td>
                      <td className="px-4 py-3 text-sm">{row.email}</td>
                      <td className="px-4 py-3 text-sm uppercase">
                        {row.platform || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {row.city
                          ? `${row.city}${row.state ? `, ${row.state}` : ''}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(row.deletionDate)}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(row.gracePeriodEnd)}</td>
                      <td className="px-4 py-3">{row.totalMessages ?? 0}</td>
                      <td className="px-4 py-3">
                        {row.gracePeriodExpired ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                            {row.canPurge ? 'Ready to purge' : 'Grace expired'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                            {row.daysUntilGraceEnd ?? 0}d left
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openDetail(row._id)}
                          className="text-pink-600 font-semibold hover:underline"
                        >
                          View archive
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 rounded-lg bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 rounded-lg bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {(selectedAccount || selectedAuditLog || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedAuditLog ? 'Legal audit trace' : 'Archived account'}
              </h2>
              <button
                onClick={closeDetail}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {detailLoading ? (
              <p className="text-gray-500">Loading...</p>
            ) : selectedAuditLog ? (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-gray-500">Email</span>
                    <p className="font-medium">{selectedAuditLog.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Profile name</span>
                    <p className="font-medium">{selectedAuditLog.profileName || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Original user ID</span>
                    <p className="font-mono text-xs break-all">{selectedAuditLog.originalUserId}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Purged at</span>
                    <p>{formatDate(selectedAuditLog.permanentDeletionDate)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Purged by</span>
                    <p>
                      {selectedAuditLog.purgedByAdminEmail || '—'}
                      {selectedAuditLog.purgedByAdminRole
                        ? ` (${selectedAuditLog.purgedByAdminRole})`
                        : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Account created</span>
                    <p>{formatDate(selectedAuditLog.accountCreatedAt)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Deleted (self-service)</span>
                    <p>{formatDate(selectedAuditLog.deletionDate)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Registration IP</span>
                    <p className="font-mono">{selectedAuditLog.registrationIp || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Platform</span>
                    <p className="uppercase">{selectedAuditLog.clientApp?.platform || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">City</span>
                    <p>
                      {selectedAuditLog.locationMeta?.city
                        ? `${selectedAuditLog.locationMeta.city}${
                            selectedAuditLog.locationMeta.state
                              ? `, ${selectedAuditLog.locationMeta.state}`
                              : ''
                          }`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Verification</span>
                    <p>
                      {selectedAuditLog.verificationState || '—'}
                      {selectedAuditLog.verificationMode
                        ? ` · ${selectedAuditLog.verificationMode}`
                        : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Actor IP</span>
                    <p className="font-mono">{selectedAuditLog.actorIp || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Request ID</span>
                    <p className="font-mono text-xs break-all">
                      {selectedAuditLog.requestId || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Legal basis</span>
                    <p>{selectedAuditLog.legalBasis || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Policy version</span>
                    <p>{selectedAuditLog.policyVersion || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Retention until</span>
                    <p>{formatDate(selectedAuditLog.retentionUntil)}</p>
                  </div>
                </div>
                {selectedAuditLog.deviceIds?.length > 0 && (
                  <p className="text-xs font-mono break-all text-gray-700">
                    Device IDs: {selectedAuditLog.deviceIds.join(', ')}
                  </p>
                )}
                {selectedAuditLog.contentCounts && (
                  <div className="p-4 bg-gray-50 rounded-xl grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(selectedAuditLog.contentCounts).map(([key, count]) => (
                      <div key={key} className="text-center p-2 bg-white rounded-lg">
                        <div className="text-lg font-bold text-pink-600">{count ?? 0}</div>
                        <div className="text-xs text-gray-500">{key}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedAuditLog.reportsTrace?.length > 0 && (
                  <div className="p-4 border rounded-xl">
                    <h3 className="font-semibold mb-2">Reports trace (metadata only)</h3>
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                      {selectedAuditLog.reportsTrace.map((r) => (
                        <li key={String(r.reportId)} className="text-xs text-gray-700">
                          {formatReportType(r.reportType)} · {r.status} · {r.role} ·{' '}
                          {formatDate(r.createdAt)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedAuditLog.purgeNotes && (
                  <p className="text-sm text-gray-600">
                    <strong>Purge notes:</strong> {selectedAuditLog.purgeNotes}
                  </p>
                )}
              </div>
            ) : selectedAccount ? (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-gray-500">Email</span>
                    <p className="font-medium">{selectedAccount.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Profile name</span>
                    <p className="font-medium">{selectedAccount.profileName || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Original user ID</span>
                    <p className="font-mono text-xs break-all">
                      {selectedAccount.originalUserId}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Deletion reason</span>
                    <p className="font-medium">{selectedAccount.deletionReason || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Deleted at</span>
                    <p>{formatDate(selectedAccount.deletionDate)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Grace period ends</span>
                    <p>
                      {formatDate(selectedAccount.gracePeriodEnd)}
                      {selectedAccount.gracePeriodExpired
                        ? ' (expired)'
                        : ` (${selectedAccount.daysUntilGraceEnd} days left)`}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-800 mb-2">Content archived</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      ['Posts', selectedAccount.contentSummary?.posts],
                      ['Messages', selectedAccount.contentSummary?.messages],
                      ['Conversations', selectedAccount.contentSummary?.conversations],
                      ['Albums', selectedAccount.contentSummary?.albums],
                      ['Reports', selectedAccount.contentSummary?.reports],
                      ['Pings', selectedAccount.contentSummary?.pings],
                      ['Discovery', selectedAccount.contentSummary?.discoverySessions],
                    ].map(([label, count]) => (
                      <div key={label} className="text-center p-2 bg-white rounded-lg">
                        <div className="text-lg font-bold text-pink-600">{count ?? 0}</div>
                        <div className="text-xs text-gray-500">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {(selectedAccount.profileSnapshot || selectedAccount.abuseSignals) && (
                  <div className="p-4 border border-slate-200 bg-slate-50 rounded-xl text-sm">
                    <h3 className="font-semibold text-slate-900 mb-3">Profile & device snapshot</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-gray-500">Account created</span>
                        <p className="font-medium">
                          {formatDate(
                            selectedAccount.profileSnapshot?.accountCreatedAt ||
                              selectedAccount.abuseSignals?.accountCreatedAt
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Username</span>
                        <p className="font-medium">
                          {selectedAccount.profileSnapshot?.username
                            ? `@${selectedAccount.profileSnapshot.username}`
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Platform</span>
                        <p className="font-medium uppercase">
                          {selectedAccount.profileSnapshot?.clientApp?.platform ||
                            selectedAccount.abuseSignals?.clientApp?.platform ||
                            '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">App version</span>
                        <p className="font-medium">
                          {selectedAccount.profileSnapshot?.clientApp?.version || '—'}
                          {selectedAccount.profileSnapshot?.clientApp?.build
                            ? ` (${selectedAccount.profileSnapshot.clientApp.build})`
                            : ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">City / region</span>
                        <p className="font-medium">
                          {selectedAccount.profileSnapshot?.locationMeta?.city
                            ? `${selectedAccount.profileSnapshot.locationMeta.city}${
                                selectedAccount.profileSnapshot.locationMeta.state
                                  ? `, ${selectedAccount.profileSnapshot.locationMeta.state}`
                                  : ''
                              }`
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Last seen</span>
                        <p className="font-medium">
                          {formatDate(selectedAccount.profileSnapshot?.lastSeen)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Verification</span>
                        <p className="font-medium">
                          {selectedAccount.profileSnapshot?.verificationState || '—'}
                          {selectedAccount.profileSnapshot?.verificationMode
                            ? ` · ${selectedAccount.profileSnapshot.verificationMode}`
                            : ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Coordinates</span>
                        <p className="font-mono text-xs break-all">
                          {selectedAccount.profileSnapshot?.location?.coordinates?.length === 2
                            ? selectedAccount.profileSnapshot.location.coordinates.join(', ')
                            : '—'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedAccount.abuseSignals && (
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl text-sm">
                    <h3 className="font-semibold text-amber-900 mb-2">Abuse signals</h3>
                    <p className="text-amber-800">
                      Registration IP:{' '}
                      <span className="font-mono">
                        {selectedAccount.abuseSignals.registrationIp || '—'}
                      </span>
                    </p>
                    {selectedAccount.abuseSignals.deviceIds?.length > 0 && (
                      <p className="text-amber-800 mt-1 break-all">
                        Device IDs: {selectedAccount.abuseSignals.deviceIds.join(', ')}
                      </p>
                    )}
                    {selectedAccount.profileSnapshot?.social && (
                      <p className="text-amber-800 mt-1">
                        Social IDs:{' '}
                        {[
                          selectedAccount.profileSnapshot.social.appleId && 'Apple',
                          selectedAccount.profileSnapshot.social.googleId && 'Google',
                          selectedAccount.profileSnapshot.social.facebookId && 'Facebook',
                        ]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </p>
                    )}
                    <p className="text-xs text-amber-700 mt-2">
                      {selectedAccount.abuseSignals.note}
                    </p>
                  </div>
                )}

                {selectedAccount.hasVerificationMedia && (
                  <div className="p-4 border border-purple-200 bg-purple-50 rounded-xl">
                    <h3 className="font-semibold text-purple-900 mb-2">
                      Verification media (archived)
                    </h3>
                    {selectedAccount.verificationSummary && (
                      <p className="text-sm text-purple-800 mb-2">
                        Mode: {selectedAccount.verificationSummary.verificationMode || '—'} ·
                        Status: {selectedAccount.verificationSummary.status || '—'}
                      </p>
                    )}
                    {selectedAccount.verificationMediaNote && (
                      <p className="text-sm text-amber-800">{selectedAccount.verificationMediaNote}</p>
                    )}
                    {selectedAccount.s3Configured === false && (
                      <p className="text-sm text-red-700 font-medium">
                        AWS is not configured on the backend. Add AWS_ACCESS_KEY_ID and
                        AWS_SECRET_ACCESS_KEY to chain_backend/.env, restart the server, then
                        reopen this archive.
                      </p>
                    )}
                    {selectedAccount.verificationMediaError && (
                      <p className="text-sm text-red-700">{selectedAccount.verificationMediaError}</p>
                    )}
                    {selectedAccount.verificationMediaErrors &&
                      Object.entries(selectedAccount.verificationMediaErrors).map(
                        ([key, msg]) =>
                          msg ? (
                            <p key={key} className="text-xs text-red-600">
                              {key}: {msg}
                            </p>
                          ) : null
                      )}
                    {selectedAccount.signedVerificationMediaUrls && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        {selectedAccount.signedVerificationMediaUrls.verificationPhoto && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Verification photo</p>
                            <img
                              src={selectedAccount.signedVerificationMediaUrls.verificationPhoto}
                              alt="Verification"
                              className="rounded-lg border max-h-48 object-contain bg-white"
                              onError={(e) => {
                                e.target.alt = 'Failed to load (S3 403 or missing file)';
                              }}
                            />
                          </div>
                        )}
                        {selectedAccount.signedVerificationMediaUrls.verificationVideo && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Verification video</p>
                            <video
                              src={selectedAccount.signedVerificationMediaUrls.verificationVideo}
                              controls
                              className="rounded-lg border max-h-48 w-full bg-black"
                            />
                          </div>
                        )}
                        {selectedAccount.signedVerificationMediaUrls.selectedProfilePicture && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Profile picture at submit</p>
                            <img
                              src={selectedAccount.signedVerificationMediaUrls.selectedProfilePicture}
                              alt="Profile at verification"
                              className="rounded-lg border max-h-48 object-contain bg-white"
                              onError={(e) => {
                                e.target.alt = 'Failed to load (URL expired or removed)';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-purple-700 mt-2">
                      Signed URLs expire in{' '}
                      {selectedAccount.verificationMediaExpiresInSec || 900}s. Role:{' '}
                      {selectedAccount.adminRole}
                    </p>
                  </div>
                )}

                {(selectedAccount.about ||
                  selectedAccount.gender ||
                  selectedAccount.isVerified != null) && (
                  <div className="p-4 border rounded-xl">
                    <h3 className="font-semibold text-gray-800 mb-2">Profile snapshot</h3>
                    <p className="text-gray-600">
                      {selectedAccount.about || 'No bio'}
                    </p>
                    <p className="mt-2 text-gray-500">
                      Verified: {selectedAccount.isVerified ? 'Yes' : 'No'}
                      {selectedAccount.gender ? ` · ${selectedAccount.gender}` : ''}
                    </p>
                  </div>
                )}

                {(selectedAccount.safetyReports?.length > 0 ||
                  selectedAccount.contentReports?.length > 0) && (
                  <div className="p-4 border rounded-xl space-y-4">
                    <h3 className="font-semibold text-gray-800">Reports (archived)</h3>
                    {selectedAccount.safetyReports?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          User safety ({selectedAccount.safetyReports.length})
                        </p>
                        <ul className="space-y-3 max-h-48 overflow-y-auto">
                          {selectedAccount.safetyReports.map((r) => (
                            <li
                              key={r._id}
                              className="p-3 bg-gray-50 rounded-lg text-sm border"
                            >
                              <div className="flex flex-wrap gap-2 mb-1">
                                <span className="font-semibold">
                                  {formatReportType(r.reportType)}
                                </span>
                                <span className="text-xs px-2 py-0.5 bg-white rounded border">
                                  {r.priority || 'low'}
                                </span>
                                <span className="text-xs text-gray-500">{r.status}</span>
                              </div>
                              <p className="text-gray-700">{r.reason}</p>
                              {r.statement && (
                                <p className="text-gray-600 mt-1 whitespace-pre-wrap">
                                  {r.statement}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                Reporter ID: {String(r.reportedBy)} ·{' '}
                                {formatDate(r.createdAt)}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedAccount.contentReports?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          Content ({selectedAccount.contentReports.length})
                        </p>
                        <ul className="space-y-2 max-h-32 overflow-y-auto">
                          {selectedAccount.contentReports.map((r) => (
                            <li
                              key={r._id}
                              className="p-2 bg-gray-50 rounded text-sm border"
                            >
                              <span className="font-medium">{r.contentType}</span> —{' '}
                              {r.reason}
                              <span className="text-xs text-gray-400 block">
                                {r.status} · {formatDate(r.createdAt)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {selectedAccount.messagesPreview?.length > 0 && (
                  <div className="p-4 border rounded-xl">
                    <h3 className="font-semibold text-gray-800 mb-2">
                      Messages (archived preview, max 30)
                    </h3>
                    <ul className="space-y-2 max-h-56 overflow-y-auto">
                      {selectedAccount.messagesPreview.map((m) => (
                        <li
                          key={m._id}
                          className="p-2 bg-gray-50 rounded text-sm border"
                        >
                          <p className="text-gray-800 whitespace-pre-wrap">{m.preview}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(m.sentAt)} · to {String(m.receiverId)} · {m.status}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedAccount.canPurge && (
                  <div className="p-4 border-2 border-red-200 bg-red-50 rounded-xl">
                    <h3 className="font-semibold text-red-900 mb-2">Purge archive</h3>
                    <p className="text-sm text-red-800 mb-3">
                      Grace period has ended. You can permanently delete messages, posts, photos,
                      and verification media. A minimal legal audit trace will be kept under{' '}
                      <strong>Purged — legal audit logs</strong>.
                    </p>
                    <textarea
                      value={purgeNotes}
                      onChange={(e) => setPurgeNotes(e.target.value)}
                      placeholder="Optional purge notes (stored in audit log)..."
                      className="w-full px-3 py-2 border rounded-lg mb-3 text-sm"
                      rows={2}
                    />
                    <button
                      type="button"
                      disabled={purging}
                      onClick={() => handlePurge(selectedAccount._id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
                    >
                      {purging ? 'Purging...' : 'Purge archive permanently'}
                    </button>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Passwords and OTPs are not shown. Verification media URLs are redacted.
                  Open safety cases against this user may remain in Reports → Safety Queue.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeletedAccounts;
